import { Component, OnDestroy } from "@angular/core";
import { BaseComponent } from "../../base.component";
import { DocumentIssueChoice } from "src/app/enums/document-issue-choice.enum";
import { MatTableDataSource } from "@angular/material/table";
import { UploadBatchValidationMetadataProperty } from "@models/upload-batch-validation-metadata-property";
import { Router } from "@angular/router";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { OkCancelDialogComponent } from "../../dialogs/ok-cancel/ok-cancel-dialog.component";
import { switchMap } from "rxjs";
import { ProcessingManager } from "src/app/managers/processing.manager";
import { UploadBatchValidationStatus } from "@models/ui/upload.model";
import { ValidationReportManager } from "src/app/managers/upload-batch-validation-report.manager";
import { UploadBatchesSdk } from "@sdks/upload-batches.sdk";

@Component({
    template: ``,
    standalone: false
})
export abstract class UploadBatchReportBaseComponent extends BaseComponent implements OnDestroy, IUploadBatchValidationReport {
    public metaTableDataSource: MatTableDataSource<UploadBatchValidationMetadataProperty>;
    public uploadBatchId: string;
    public uploadBatchTitle: string;
    public uploadedTimestamp: string;
    public dateTimeRun: string;
    public errorData: ChartSeries[];
    public warningData: ChartSeries[];
    public infoData: ChartSeries[];
    public showErrors: boolean = false;
    public showWarnings: boolean = false;
    public showInfo: boolean = false;
    public uploadBatchStatus: UploadBatchValidationStatus;
    public refreshInterval;

    get ValidationStatus(): typeof UploadBatchValidationStatus { return UploadBatchValidationStatus; }

    public missingNativeFileLabel = 'Missing Native Files';
    public missingSourceFileLabel = 'Missing Source Files';
    public missingNormalizedPdfLabel = 'Missing Normalized PDF File';
    public failedToIndexLabel = 'Index Incomplete';
    public failedToConvertLabel = 'Failed to convert';
    public failedToExtractFilesLabel = 'Container File Extraction Failure';
    public pageCountLabel = 'Page Count Threshold';
    public unsupportedFileLabel = 'Unsupported File';

    private labelChoiceMap: Map<string, DocumentIssueChoice> = new Map<string, DocumentIssueChoice>([
        [this.missingSourceFileLabel, DocumentIssueChoice.FailedToUploadOriginal],
        [this.missingNativeFileLabel, DocumentIssueChoice.FailedToUploadOriginal],
        [this.missingNormalizedPdfLabel, DocumentIssueChoice.MissingNormalizedPdfFile],
        [this.failedToIndexLabel, DocumentIssueChoice.FailedToIndex],
        [this.failedToConvertLabel, DocumentIssueChoice.FailedToConvertToPdf],
        [this.failedToExtractFilesLabel, DocumentIssueChoice.FailedToExtractContainerFile],
        [this.pageCountLabel, DocumentIssueChoice.ExceedsPageCountThreshold],
        [this.unsupportedFileLabel, DocumentIssueChoice.UnsupportedFileType],
    ]);

    constructor(public router: Router,
        public matDialog: MatDialog,
        public processingManager: ProcessingManager,
        public reportManager: ValidationReportManager,
        public uploadBatchesSdk: UploadBatchesSdk) {
        super();

        this.refreshInterval = setInterval(() => this.refreshValidationData(), 10000);
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
        this.errorData = [];
        this.warningData = [];
        this.infoData = [];
        this.metaTableDataSource = null;
        clearInterval(this.refreshInterval);
    }

    public scheduleJobBatchValidation(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = true;
        dialogConfig.data = {
            title: `Run Task`,
            message: `All processing must be complete before running this step. Are you sure you would like to run the selected task?`
        };

        this.matDialog.open(OkCancelDialogComponent, dialogConfig).afterClosed()
            .pipe(
                switchMap((okClicked) => {
                    if (okClicked) {
                        return this.uploadBatchesSdk.validate([this.uploadBatchId]);
                    }
                })
            )
            .subscribe();
    }

    public select(evt): void {
        const choice = this.labelChoiceMap.get(evt.name);
        if (choice)
            this.router.navigate(['/review/browse'], { queryParams: { UploadBatchId: this.uploadBatchId, Issues: choice } });
        else
            console.warn(`Unknown event: ${choice}`);
    }

    abstract refreshValidationData(): void;
}
interface ChartSeries {
    name: string,
    value: string
}

interface IUploadBatchValidationReport {
    refreshValidationData(): void;
}
